import React, { useEffect, useMemo, useState } from 'react';
import { photoHintsOf } from '../lib/storePhotos';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import PriceChart from '../components/PriceChart';
import PriceDisplay from '../components/PriceDisplay';
import PageMeta from '../components/PageMeta';
import NearMeFilter, { formatDistanceKm, NearMeChip } from '../components/NearMeFilter';
import CollectButtons from '../components/CollectButtons';
import { loadPrefs, savePrefs, sellerOfListing, SELLER_EBAY, SELLER_STORE, useNearMePref, useSortPref } from '../lib/listingPrefs';
import SealedArt from '../components/SealedArt';
import SealedProductsSection from '../components/SealedProductsSection';
import { TypeBadge } from '../components/ProductTile';
import { gameLabel } from '../lib/games';
import { EXCLUSIVE_LABELS, groupOfType } from '../lib/productTypes';
import { priceLabel, priceHint, NO_SALES_TEXT } from '../lib/priceLabels';
import SeoExtras from '../components/SeoExtras';

// WO-10: sealed product page (/product/:productId). Same data as a card page, keyed by the product id: TCGplayer
// price (tcgcsv), price history (bucket 'sealed'), live store / eBay listings and the near-me filter.
// Card pages (pages/Card.js) are not touched.
// WO-20: game-aware breadcrumbs (game / series / set / sealed kind), drawn placeholder per kind, key facts, a
// listing summary, structured data (schema.org Product; offers only from real listing prices) and more from the set.

function useJsonLd(id, data) {
  const json = data ? JSON.stringify(data) : '';
  useEffect(() => {
    if (!json || typeof document === 'undefined') return undefined;
    // WO-42: the server-rendered page already carries this page's Product JSON-LD (data-lc-seo = its canonical URL);
    // a second, client-built Product block would give crawlers two different descriptions of one product.
    const canonical = `https://lilycove.io${window.location.pathname}`;
    if (document.head.querySelector(`script[data-lc-seo="${canonical}"]`)) return undefined;
    let tag = document.getElementById(id);
    if (!tag) {
      tag = document.createElement('script');
      tag.type = 'application/ld+json';
      tag.id = id;
      document.head.appendChild(tag);
    }
    tag.textContent = json;
    return () => { tag.remove(); };
  }, [id, json]);
}

const SORTS = ['Lowest Price', 'Highest Price', 'Most Recent', 'Nearest'];
const SELLERS = [['all', 'All sellers'], [SELLER_STORE, 'Stores'], [SELLER_EBAY, 'eBay']];
const CHART_SOURCES = ['tcgplayer', 'shopify_active', 'ebay_active'];

const ProductPage = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const apiUrl = process.env.REACT_APP_API_URL || '';
  const [product, setProduct] = useState(null);
  const [status, setStatus] = useState('loading');
  const [listings, setListings] = useState([]);
  const [listingsStatus, setListingsStatus] = useState('loading');
  // WO-35: sort, seller type and near me follow the shopper across pages and visits (lib/listingPrefs.js).
  const [sortOrder, setSortOrder] = useSortPref('Lowest Price', SORTS);
  const [seller, setSellerState] = useState(() => {
    const s0 = loadPrefs().sellers;
    return s0 && s0.length === 1 ? s0[0] : 'all';
  });
  const setSeller = (v) => { setSellerState(v); savePrefs({ sellers: v === 'all' ? [] : [v] }); };
  const [nearOpen, setNearOpen] = useState(true);
  const [nearMe, setNearMe, nearMeWanted] = useNearMePref();
  const [nearbyById, setNearbyById] = useState(null);
  const [nearMeStatus, setNearMeStatus] = useState('idle');
  const [photoBroken, setPhotoBroken] = useState(false);   // WO-34: the store photo failed to load -> mockup

  const encodedId = encodeURIComponent(productId || '');
  const [moreFromSet, setMoreFromSet] = useState([]);

  useEffect(() => {
    const ctrl = new AbortController();
    setStatus('loading');
    setPhotoBroken(false);
    fetch(`${apiUrl}/api/products/${encodedId}`, { signal: ctrl.signal })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(r.status === 404 ? 'not_found' : `HTTP ${r.status}`))))
      .then((data) => {
        if (data.kind === 'single' && data.url) {
          window.location.replace(data.url);   // a card id: its card page is the canonical page
          return;
        }
        setProduct(data);
        setStatus('ready');
      })
      .catch((e) => {
        if (e.name === 'AbortError') return;
        // WO-35: product ids are lowercase; /product/ME5.ETB opens /product/me5.etb
        if (e.message === 'not_found' && productId && productId !== productId.toLowerCase()) {
          navigate(`/product/${encodeURIComponent(productId.toLowerCase())}`, { replace: true });
          return;
        }
        setStatus(e.message === 'not_found' ? 'not_found' : 'error');
      });
    return () => ctrl.abort();
  }, [apiUrl, encodedId, productId, navigate]);

  useEffect(() => {
    const ctrl = new AbortController();
    setListingsStatus('loading');
    fetch(`${apiUrl}/api/products/${encodedId}/listings`, { signal: ctrl.signal })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((data) => { setListings(Array.isArray(data) ? data : []); setListingsStatus('ready'); })
      .catch((e) => { if (e.name !== 'AbortError') setListingsStatus('error'); });
    return () => ctrl.abort();
  }, [apiUrl, encodedId]);

  useEffect(() => {
    if (!nearMe.active || nearMe.lat === null || nearMe.lng === null) {
      setNearbyById(null);
      setNearMeStatus('idle');
      return undefined;
    }
    const ctrl = new AbortController();
    setNearMeStatus('loading');
    const params = new URLSearchParams({ near_lat: String(nearMe.lat), near_lng: String(nearMe.lng), radius_km: String(nearMe.radiusKm) });
    fetch(`${apiUrl}/api/products/${encodedId}/listings?${params}`, { signal: ctrl.signal })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((data) => {
        const byId = {};
        (Array.isArray(data) ? data : []).forEach((item) => {
          byId[item.itemId] = { distance_km: item.distance_km, store_location: item.store_location };
        });
        setNearbyById(byId);
        setNearMeStatus('ready');
      })
      .catch((e) => { if (e.name !== 'AbortError') { setNearbyById(null); setNearMeStatus('error'); } });
    return () => ctrl.abort();
  }, [apiUrl, encodedId, nearMe.active, nearMe.lat, nearMe.lng, nearMe.radiusKm]);

  useEffect(() => {
    if (!nearMe.active && sortOrder === 'Nearest') setSortOrder('Lowest Price', { remember: false });
  }, [nearMe.active, sortOrder, setSortOrder]);

  const productSetId = product?.set_id;
  useEffect(() => {
    if (!productSetId) { setMoreFromSet([]); return undefined; }
    const ctrl = new AbortController();
    fetch(`${apiUrl}/api/products?set_id=${encodeURIComponent(productSetId)}&limit=60`, { signal: ctrl.signal })
      .then((r) => (r.ok ? r.json() : { results: [] }))
      .then((data) => setMoreFromSet((Array.isArray(data?.results) ? data.results : []).filter((p) => p.product_id !== productId)))
      .catch(() => {});
    return () => ctrl.abort();
  }, [apiUrl, productSetId, productId]);

  const listingPrices = useMemo(() => listings.map((l) => l.price).filter((x) => Number.isFinite(x) && x > 0), [listings]);
  useJsonLd('product-jsonld', product && status === 'ready' ? {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    category: `${gameLabel(product.game_id)} TCG > Sealed > ${product.type_name}`,
    brand: { '@type': 'Brand', name: gameLabel(product.game_id) },
    url: `https://lilycove.io/product/${encodedId}`,
    ...(product.release_date ? { releaseDate: String(product.release_date).slice(0, 10) } : {}),
    ...(listingPrices.length ? {
      offers: {
        '@type': 'AggregateOffer', priceCurrency: 'CAD', offerCount: listingPrices.length,
        lowPrice: Math.min(...listingPrices).toFixed(2), highPrice: Math.max(...listingPrices).toFixed(2),
      },
    } : {}),
  } : null);

  const shown = useMemo(() => {
    let rows = listings.map((l) => (nearbyById && nearbyById[l.itemId] ? { ...l, ...nearbyById[l.itemId] } : l));
    if (nearbyById) rows = rows.filter((l) => nearbyById[l.itemId]);
    if (seller !== 'all') rows = rows.filter((l) => sellerOfListing(l) === seller);
    const price = (l) => (Number.isFinite(l.price) ? l.price : Infinity);
    const sorters = {
      'Lowest Price': (a, b) => price(a) - price(b),
      'Highest Price': (a, b) => price(b) - price(a),
      'Most Recent': (a, b) => String(b.date_listed || '').localeCompare(String(a.date_listed || '')),
      Nearest: (a, b) => (a.distance_km ?? Infinity) - (b.distance_km ?? Infinity),
    };
    return [...rows].sort(sorters[sortOrder] || sorters['Lowest Price']);
  }, [listings, nearbyById, sortOrder, seller]);

  if (status === 'loading') {
    return <div className="py-12 text-cream/70">Loading…</div>;
  }
  if (status !== 'ready' || !product) {
    return (
      <div className="py-12 text-cream">
        <PageMeta title="Product not found | Lilycove" />
        <h1 className="text-2xl text-purple mb-2">Product not found</h1>
        <p className="text-cream/70">We could not find this product. <Link to="/" className="text-skyblue">Back to home</Link></p>
      </div>
    );
  }

  const tcgPrice = product.tcgplayer_price?.price;
  const attrs = product.attributes || {};
  const exclusive = Array.isArray(attrs.exclusive) ? attrs.exclusive : [];
  const exclusiveLabel = EXCLUSIVE_LABELS;
  const game = gameLabel(product.game_id);
  const group = groupOfType(product.product_type, product.type_name);
  const title = `${product.name} Price | ${game} ${product.type_name} | Lilycove`;
  const description = `${product.name} (${game} ${product.type_name}${product.set_name ? `, ${product.set_name}` : ''})`
    + `${tcgPrice ? `: TCGplayer ${priceLabel(product.tcgplayer_price?.price_type).toLowerCase()} CA$${Number(tcgPrice).toFixed(2)}` : ''}`
    + `. Compare store and eBay listings${listingPrices.length ? ` (${listingPrices.length} live)` : ''} and see the price history.`;
  const lowestListing = listingPrices.length ? Math.min(...listingPrices) : null;
  const facts = [
    ['Type', product.type_name],
    ['Game', game],
    product.set_name ? ['Set', <Link key="s" to={`/sets/${encodeURIComponent(product.set_id)}`} className="text-skyblue hover:text-cream">{product.set_name}</Link>] : null,
    product.release_date ? ['Release date', `${String(product.release_date).slice(0, 10)}${product.status === 'presale' ? ' (pre-release)' : ''}`] : null,
    attrs.pack_count ? ['Packs inside', attrs.pack_count] : null,
    attrs.units ? ['Units', attrs.units] : null,
    exclusive.length ? ['Exclusive', exclusive.map((x) => exclusiveLabel[x] || x).join(', ')] : null,
    product.tcgplayer_price?.usd ? ['TCGplayer (USD)', `US$${Number(product.tcgplayer_price.usd).toFixed(2)}`] : null,
  ].filter(Boolean);

  return (
    <div>
      <PageMeta title={title} description={description} url={`https://lilycove.io/product/${encodedId}`} />
      <div className="flex items-center text-sm mb-6 text-cream/70">
        <Link to="/" className="text-skyblue hover:text-cream">Home</Link>
        <span className="mx-2">/</span>
        <Link to={product.game_id && product.game_id !== 'pokemon' ? `/series?game=${encodeURIComponent(product.game_id)}` : '/series'}
              className="text-skyblue hover:text-cream">{game} Series</Link>
        {product.series_id && product.series_name && (
          <>
            <span className="mx-2">/</span>
            <Link to={`/series/${encodeURIComponent(product.series_id)}`} className="text-skyblue hover:text-cream">{product.series_name}</Link>
          </>
        )}
        {product.set_id && product.set_name && (
          <>
            <span className="mx-2">/</span>
            <Link to={`/sets/${encodeURIComponent(product.set_id)}`} className="text-skyblue hover:text-cream">{product.set_name}</Link>
          </>
        )}
        <span className="mx-2">/</span>
        <Link to={product.set_id ? `/sets/${encodeURIComponent(product.set_id)}?tab=sealed` : `/sealed?type=${group.key}`}
              className="text-skyblue hover:text-cream">Sealed</Link>
      </div>

      <div className="border-b border-purple/30 pb-4 mb-6 flex flex-col md:flex-row justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-medium text-purple" data-testid="product-name">{product.name}</h1>
          <div className="flex flex-wrap items-center gap-1 mt-2 text-sm text-cream/80">
            <Link to={`/sealed?type=${group.key}`}><TypeBadge tone="sealed">{product.type_name}</TypeBadge></Link>
            {product.status === 'presale' && <TypeBadge tone="presale">Pre-release</TypeBadge>}
            {exclusive.map((x) => (
              <span key={x} className="inline-flex items-center rounded-full bg-dark border border-purple/50 px-2 py-0.5 text-xs font-medium">
                {exclusiveLabel[x] || x} exclusive
              </span>
            ))}
            {attrs.art && <span className="text-cream/60 ml-1">{attrs.art}</span>}
            {product.release_date && <span className="text-cream/60 ml-2">Released {product.release_date}</span>}
          </div>
        </div>
        <div className="text-right">
          <div className="text-xs text-cream/60 mb-1" title={priceHint(product.tcgplayer_price?.price_type)}>{priceLabel(product.tcgplayer_price?.price_type)}</div>
          <div className="text-2xl md:text-3xl font-bold text-green" data-testid="product-price">
            {tcgPrice ? <PriceDisplay amountCad={tcgPrice} /> : <span className="text-lg text-cream/60">{NO_SALES_TEXT}</span>}
          </div>
          {tcgPrice ? <div className="text-[11px] text-cream/50 mt-1">{priceHint(product.tcgplayer_price?.price_type)}</div> : null}
          {product.tcgplayer_price?.date && (
            <div className="text-xs text-cream/50 mt-1">as of {String(product.tcgplayer_price.date).slice(0, 10)}</div>
          )}
          <div className="mt-3">
            <CollectButtons itemId={product.product_id || productId} itemName={product.name} sealed
                            currentPrice={tcgPrice || null} returnPath={`/product/${encodedId}`} />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1">
          <div className="rounded-lg border border-purple/30 bg-dark overflow-hidden">
            <SealedArt key={product.product_id} productType={product.product_type} typeName={product.type_name}
                       image={product.image_large || product.image_small} alt={product.name}
                       className="aspect-[4/3] lg:aspect-square w-full"
                       cover={product.cover} setCode={product.set_code} coverSize="large" art={product.art_image}
                       photo={product.photo_url} photoCredit={product.photo_credit} onPhotoBroken={() => setPhotoBroken(true)}
                       photoHints={photoHintsOf(product)} />
            {!(product.image_large || product.image_small) && product.photo_url && !photoBroken && (
              <p className="px-3 py-1.5 text-[11px] text-cream/50 border-t border-purple/20" data-testid="photo-credit">
                {product.photo_link ? (
                  <a href={product.photo_link} target="_blank" rel="noopener noreferrer nofollow" className="underline hover:text-cream">
                    {product.photo_credit || 'Photo: store listing'}
                  </a>
                ) : (product.photo_credit || 'Photo: store listing')}
                <span className="text-cream/35"> (from their listing of this product)</span>
              </p>
            )}
            {!(product.image_large || product.image_small) && !(product.photo_url && !photoBroken) && product.art_image && (
              <p className="px-3 py-1.5 text-[11px] text-cream/40 border-t border-purple/20">Illustration with cards from this set, not a photo of the product</p>
            )}
          </div>
          <dl className="mt-4 rounded-lg border border-purple/20 bg-dark divide-y divide-purple/10 text-sm" data-testid="product-facts">
            {facts.map(([k, v]) => (
              <div key={k} className="flex justify-between gap-4 px-3 py-2">
                <dt className="text-cream/50">{k}</dt>
                <dd className="text-cream text-right">{v}</dd>
              </div>
            ))}
            <div className="flex justify-between gap-4 px-3 py-2">
              <dt className="text-cream/50">Live listings</dt>
              <dd className="text-cream text-right">
                {listingsStatus === 'ready' ? (listingPrices.length
                  ? <>{listingPrices.length} · from <PriceDisplay amountCad={lowestListing} /></>
                  : 'None yet') : '…'}
              </dd>
            </div>
          </dl>
        </div>
        <div className="lg:col-span-2">
          <h2 className="text-lg text-cream mb-2">Price history</h2>
          <PriceChart cardId={encodedId} cardName={product.name} bucket="sealed" source="tcgplayer" sources={CHART_SOURCES} />
        </div>
      </div>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="lg:col-span-1">
          <NearMeFilter
            open={nearOpen}
            onToggle={() => setNearOpen((v) => !v)}
            profile={profile}
            nearMe={{ ...nearMe, status: nearMeStatus }}
            onChange={({ status: _s, ...next }) => setNearMe(next)}
            matchCount={nearbyById ? `${Object.keys(nearbyById).length} listings` : ''}
          />
        </div>
        <div className="lg:col-span-3">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <h2 className="text-lg text-cream">Listings ({shown.length})</h2>
            <div className="flex flex-wrap items-center gap-2">
              <NearMeChip profile={profile} nearMe={nearMe} onChange={setNearMe} status={nearMeStatus} wanted={nearMeWanted} />
              <select value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} aria-label="Sort listings"
                      className="bg-dark border border-purple/30 rounded px-2 py-1 text-sm text-cream">
                {SORTS.filter((s) => s !== 'Nearest' || nearMe.active).map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mb-3" data-testid="listing-seller-filter">
            {SELLERS.map(([key, label]) => (
              <button key={key} type="button" onClick={() => setSeller(key)} aria-pressed={seller === key}
                      className={`px-3 py-1 rounded-full text-xs border transition ${seller === key
                        ? 'bg-purple border-purple text-cream' : 'border-purple/40 text-cream/70 hover:text-cream'}`}>
                {label}
              </button>
            ))}
          </div>
          {listingsStatus === 'loading' && <div className="text-cream/60">Loading listings…</div>}
          {listingsStatus === 'error' && <div className="text-cream/60">Listings are unavailable right now.</div>}
          {listingsStatus === 'ready' && shown.length === 0 && (
            <div className="rounded-lg border border-purple/20 bg-dark p-4 text-cream/60 text-sm">
              {nearbyById
                ? `No store listings within ${nearMe.radiusKm} km of ${nearMe.label || 'you'} for this product. Try a larger radius or turn off near me.`
                : seller !== 'all' && listings.length
                  ? `No ${seller === SELLER_EBAY ? 'eBay' : 'store'} listings for this product right now (${listings.length} from other sellers).`
                  : 'No store or eBay listings for this product yet. We check stores and eBay continuously; the TCGplayer market price above is updated daily.'}
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="product-listings">
            {shown.map((l) => (
              <a key={l.itemId} href={l.itemWebUrl} target="_blank" rel="noopener noreferrer"
                 className="block rounded-lg border border-purple/30 bg-dark hover:border-purple/60 transition">
                <div className="p-3">
                  <div className="text-xs text-cream/60 mb-1">{l.source === 'ebay' ? 'eBay' : 'Store'}</div>
                  <h3 className="text-sm font-medium text-cream">{l.title}</h3>
                  <div className="mt-1 text-xs text-cream/60">
                    {l.source === 'shopify' && l.store && <span>{l.store}</span>}
                    {Number.isFinite(l.distance_km) && (
                      <span className="ml-2 text-skyblue/80">
                        {formatDistanceKm(l.distance_km)}{l.store_location?.city ? ` · ${l.store_location.city}` : ''}
                      </span>
                    )}
                    {l.source === 'ebay' && l.location && <span>{l.location}</span>}
                  </div>
                  <div className="mt-2 text-skyblue font-medium">
                    <PriceDisplay amountCad={l.price} fallback="Price unavailable" />
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </div>

      {moreFromSet.length > 0 && (
        <SealedProductsSection
          items={moreFromSet.slice(0, 10)}
          title={`More sealed from ${product.set_name}`}
          total={moreFromSet.length}
          seeAllTo={moreFromSet.length > 10 ? `/sets/${encodeURIComponent(product.set_id)}?tab=sealed` : null}
          seeAllLabel="See all"
        />
      )}
      <SeoExtras />
    </div>
  );
};

export default ProductPage;
